import React from "react";
import { Box, Typography, Container, IconButton, Link } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import { usePublicSiteSettings } from "../../hooks/useSiteSettings";

export const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const { data: siteSettings } = usePublicSiteSettings();

  const instagramUrl = siteSettings?.settings?.social_instagram;
  const facebookUrl = siteSettings?.settings?.social_facebook;
  const contactEmail = siteSettings?.settings?.contact_email;
  const contactPhone =
    siteSettings?.settings?.contact_phone || "(555) 123-4567"; // Hardcoded fallback

  return (
    <Box
      component="footer"
      sx={{
        borderTop: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.default",
        pt: 4,
        pb: 4,
        mt: { xs: 6, md: 8 }, // Padding above the border line
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          {/* Contact Info */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
            }}
          >
            {contactEmail && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <EmailIcon sx={{ fontSize: "1rem", color: "text.secondary" }} />
                <Link
                  href={`mailto:${contactEmail}`}
                  sx={{
                    fontFamily: '"Playfair Display", serif',
                    color: "text.secondary",
                    textDecoration: "none",
                    fontSize: "0.875rem",
                    letterSpacing: "0.05em",
                    "&:hover": {
                      color: "text.primary",
                    },
                  }}
                >
                  {contactEmail}
                </Link>
              </Box>
            )}
            {contactPhone && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <PhoneIcon sx={{ fontSize: "1rem", color: "text.secondary" }} />
                <Link
                  href={`tel:${contactPhone}`}
                  sx={{
                    fontFamily: '"Playfair Display", serif',
                    color: "text.secondary",
                    textDecoration: "none",
                    fontSize: "0.875rem",
                    letterSpacing: "0.05em",
                    "&:hover": {
                      color: "text.primary",
                    },
                  }}
                >
                  {contactPhone}
                </Link>
              </Box>
            )}
          </Box>

          {/* Social Links */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
            }}
          >
            {instagramUrl ? (
              <IconButton
                component="a"
                href={instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Visit Mariah Blue Photography on Instagram"
                sx={{
                  color: "text.secondary",
                  "&:hover": {
                    color: "text.primary",
                  },
                }}
              >
                <InstagramIcon />
              </IconButton>
            ) : (
              <IconButton
                disabled
                aria-label="Instagram link not available"
                sx={{
                  color: "text.disabled",
                }}
              >
                <InstagramIcon />
              </IconButton>
            )}
            {facebookUrl ? (
              <IconButton
                component="a"
                href={facebookUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Visit Mariah Blue Photography on Facebook"
                sx={{
                  color: "text.secondary",
                  "&:hover": {
                    color: "text.primary",
                  },
                }}
              >
                <FacebookIcon />
              </IconButton>
            ) : (
              <IconButton
                disabled
                aria-label="Facebook link not available"
                sx={{
                  color: "text.disabled",
                }}
              >
                <FacebookIcon />
              </IconButton>
            )}
          </Box>

          {/* Footer Links */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
              justifyContent: "center",
              mb: 1,
            }}
          >
            <Link
              component={RouterLink}
              to="/sitemap"
              sx={{
                fontFamily: '"Playfair Display", serif',
                color: "text.secondary",
                textDecoration: "none",
                fontSize: "0.75rem",
                letterSpacing: "0.05em",
                "&:hover": {
                  color: "text.primary",
                },
              }}
            >
              Sitemap
            </Link>
          </Box>

          {/* Copyright */}
          <Typography
            variant="caption"
            sx={{
              fontFamily: '"Playfair Display", serif',
              color: "text.secondary",
              textTransform: "uppercase",
              fontSize: "0.75rem",
              letterSpacing: "0.1em",
            }}
          >
            © {currentYear} Mariah Blue Photography
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};
